import { useMemo, useState } from 'react';
import {
  Check,
  Loader2,
  MessageSquare,
  Paperclip,
  Pencil,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import {
  supportRequestService,
  type RequestMessage,
} from '../../services/supportRequest.service';
import {
  useDeleteMessage,
  useDetachFromMessage,
  useEditMessage,
  useRequestArtifacts,
  useRequestAttachments,
} from '../../hooks/useSupportRequests';
import { useUserConfig } from '../../hooks/useUserConfig';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { formatBytes } from './time';
import { RelativeTime } from './requestUi';
import { ArtifactCard } from './ArtifactCard';
import { MarkdownText } from '../../components/chat/MarkdownText';

/**
 * One message, used by both the conversation and the thread panel.
 *
 * Attachments and artifacts are looked up from request-level queries rather than
 * passed down, so a row can be dropped anywhere without threading maps through
 * every parent. React Query dedupes the fetches, so N rows still cost one
 * request each for attachments and artifacts.
 */
export function MessageRow({
  message,
  requestId,
  compact = false,
  onOpenArtifact,
  /** Id of the artifact already open full-screen, if any — its card drops the
   *  redundant "View full". */
  openArtifactId,
  replyCount = 0,
  unread = false,
  unreadReplies = 0,
  onOpenThread,
}: {
  message: RequestMessage;
  requestId: string;
  compact?: boolean;
  onOpenArtifact?: (artifactId: string, messageId: string) => void;
  openArtifactId?: string | null;
  replyCount?: number;
  /** This message arrived after the reader last opened the request. */
  unread?: boolean;
  /** How many replies in this message's thread are unread. */
  unreadReplies?: number;
  onOpenThread?: () => void;
}) {
  const { data: attachments } = useRequestAttachments(requestId);
  const { data: artifacts } = useRequestArtifacts(requestId);
  const { data: userConfig } = useUserConfig();

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(message.body);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const editMessage = useEditMessage(requestId);
  const deleteMessage = useDeleteMessage(requestId);
  const detach = useDetachFromMessage(requestId);

  // Only the author edits their own words — deliberately narrower than the
  // request-level controls, which staff and admins also get. An agent message
  // belongs to the staff member who triggered it.
  const isMine =
    !!userConfig?.frontegg_id &&
    (message.author_id === userConfig.frontegg_id ||
      message.posted_by_actor_id === userConfig.frontegg_id);

  const myAttachments = useMemo(
    () =>
      (message.attachment_ids ?? [])
        .map((id) => attachments?.find((a) => a.id === id))
        .filter((a): a is NonNullable<typeof a> => !!a),
    [message.attachment_ids, attachments],
  );

  const myArtifacts = useMemo(
    () =>
      (message.artifact_ids ?? [])
        .map((id) => artifacts?.find((a) => a.id === id))
        .filter((a): a is NonNullable<typeof a> => !!a),
    [message.artifact_ids, artifacts],
  );

  if (message.author_type === 'system') {
    return (
      <div className="flex items-center gap-2 py-1">
        <Separator className="flex-1" />
        <span className="text-xs text-muted-foreground px-2 text-center">
          {message.body} · <RelativeTime iso={message.created_at} />
        </span>
        <Separator className="flex-1" />
      </div>
    );
  }

  const isPliable = message.author_type === 'pliable';
  const isEllie = message.author_type === 'ellie';
  const who = message.author_label || (isEllie ? 'Ellie' : 'Someone');
  const avatarSize = compact ? 'h-6 w-6 text-[10px]' : 'h-8 w-8 text-xs';

  return (
    <div className="flex gap-3 group/message">
      <div
        className={`${avatarSize} rounded-full flex items-center justify-center font-medium flex-shrink-0 ${
          isEllie
            ? 'bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-300'
            : isPliable
              ? 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300'
              : 'bg-muted text-muted-foreground'
        }`}
      >
        {isEllie ? <Sparkles className={compact ? 'h-3 w-3' : 'h-4 w-4'} /> : initials(who)}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          {/* A dot rather than a count: a root message is one message, so a
              number would only ever say "1". */}
          {unread && (
            <span
              className="h-2 w-2 rounded-full bg-primary flex-shrink-0 self-center"
              role="status"
              aria-label="Unread"
            />
          )}
          <span className="text-sm font-medium text-foreground">{who}</span>
          {isPliable && (
            <Badge variant="outline" className="h-4 px-1 text-[10px] font-normal">
              Pliable
            </Badge>
          )}
          <span className="text-xs text-muted-foreground">
            <RelativeTime iso={message.created_at} />
          </span>

          {isMine && !editing && (
            <span className="opacity-0 group-hover/message:opacity-100 transition-opacity flex items-center gap-0.5">
              <button
                type="button"
                onClick={() => {
                  setDraft(message.body);
                  setEditing(true);
                }}
                title="Edit message"
                aria-label="Edit message"
                className="rounded p-1 text-muted-foreground hover:text-foreground hover:bg-accent"
              >
                <Pencil className="h-3 w-3" />
              </button>
              <button
                type="button"
                onClick={() => setConfirmDelete(true)}
                title="Delete message"
                aria-label="Delete message"
                className="rounded p-1 text-muted-foreground hover:text-destructive hover:bg-accent"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </span>
          )}
        </div>

        {editing ? (
          <div className="mt-1 space-y-2">
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setEditing(false);
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  editMessage
                    .mutateAsync({ messageId: message.id, body: draft })
                    .then(() => setEditing(false));
                }
              }}
              rows={3}
              // Vertical only: a horizontal drag would push the box past the
              // message column. The base Textarea's min-h is the floor, so the
              // handle can't be dragged down to nothing.
              className="resize-y text-sm"
              autoFocus
            />
            <div className="flex items-center gap-1">
              <Button
                size="sm"
                className="h-7"
                disabled={editMessage.isPending}
                onClick={() =>
                  editMessage
                    .mutateAsync({ messageId: message.id, body: draft })
                    .then(() => setEditing(false))
                }
              >
                {editMessage.isPending ? (
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                ) : (
                  <Check className="h-3 w-3 mr-1" />
                )}
                Save
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-7"
                onClick={() => {
                  setDraft(message.body);
                  setEditing(false);
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          message.body && (
            <div className="mt-0.5">
              <MarkdownText
                content={message.body}
                className="text-sm text-foreground [&_p]:mb-2 [&_p:last-child]:mb-0"
              />
              {message.edited_at && (
                <span className="text-[11px] text-muted-foreground">(edited)</span>
              )}
            </div>
          )
        )}

        {myAttachments.map((a) => (
          <AttachmentChip
            key={a.id}
            requestId={requestId}
            attachment={a}
            onRemove={
              isMine
                ? () =>
                    detach.mutate({ messageId: message.id, attachmentId: a.id })
                : undefined
            }
          />
        ))}

        {myArtifacts.map((artifact) => (
          <ArtifactCard
            key={artifact.id}
            artifact={artifact}
            showViewFull={artifact.id !== openArtifactId}
            onOpen={() => onOpenArtifact?.(artifact.id, message.id)}
            onRemove={
              isMine
                ? () =>
                    detach.mutate({ messageId: message.id, artifactId: artifact.id })
                : undefined
            }
          />
        ))}

        {/* Thread affordance — only in the main conversation, never inside a
            thread, since replies cannot themselves be replied to. */}
        {onOpenThread && (
          <button
            type="button"
            onClick={onOpenThread}
            className={`mt-1.5 inline-flex items-center gap-1.5 text-xs transition-colors ${
              unreadReplies > 0
                ? 'text-foreground font-medium'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <MessageSquare className="h-3 w-3" />
            {replyCount > 0
              ? `${replyCount} ${replyCount === 1 ? 'reply' : 'replies'}`
              : 'Reply in thread'}
            {/* The count, not just a dot: the whole point is to say whether
                opening the thread is worth it. */}
            {unreadReplies > 0 && (
              <span
                className="inline-flex items-center rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-semibold leading-none text-primary-foreground"
                aria-label={`${unreadReplies} unread ${
                  unreadReplies === 1 ? 'reply' : 'replies'
                }`}
              >
                {unreadReplies} new
              </span>
            )}
          </button>
        )}
      </div>

      {confirmDelete && (
        <AlertDialog open onOpenChange={setConfirmDelete}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this message?</AlertDialogTitle>
              <AlertDialogDescription>
                It will disappear for everyone on the request. Replies to it stay
                — they're other people's words. Pliable keeps the record
                internally.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleteMessage.isPending}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => {
                  e.preventDefault();
                  deleteMessage
                    .mutateAsync(message.id)
                    .then(() => setConfirmDelete(false));
                }}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {deleteMessage.isPending && (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                )}
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}

export function AttachmentChip({
  requestId,
  attachment,
  onRemove,
}: {
  requestId: string;
  attachment: { id: string; filename: string; size: number };
  onRemove?: () => void;
}) {
  const [busy, setBusy] = useState(false);

  const open = async () => {
    setBusy(true);
    try {
      const url = await supportRequestService.downloadUrl(requestId, attachment.id);
      window.open(url, '_blank', 'noopener');
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={open}
      disabled={busy}
      title={attachment.filename}
      // max-w-xs alone (20rem) is wider than the 16rem file sidebar, so a long
      // filename pushed the chip past the sidebar edge. Cap at the smaller of
      // the two: never wider than the container, still bounded in a wide one.
      className="mt-2 mr-2 inline-flex items-center gap-1.5 rounded border border-border bg-muted/40 px-2 py-1 text-xs hover:bg-accent transition-colors max-w-[min(20rem,100%)]"
    >
      {busy ? (
        <Loader2 className="h-3 w-3 animate-spin flex-shrink-0" />
      ) : (
        <Paperclip className="h-3 w-3 flex-shrink-0" />
      )}
      <span className="truncate min-w-0">{attachment.filename}</span>
      {!!attachment.size && (
        <span className="text-muted-foreground flex-shrink-0">
          {formatBytes(attachment.size)}
        </span>
      )}
      {onRemove && (
        // A span, not a button — this chip is itself a button and nesting one
        // inside another is invalid HTML and breaks click handling.
        <span
          role="button"
          tabIndex={0}
          aria-label={`Remove ${attachment.filename}`}
          title="Remove this file from the message"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              onRemove();
            }
          }}
          className="flex-shrink-0 text-muted-foreground hover:text-destructive"
        >
          <X className="h-3 w-3" />
        </span>
      )}
    </button>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}
